import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Utensils, Calendar, MapPin, Clock, Trash2, Copy, Plane, Hotel, Car, Ticket, FileText, Phone } from 'lucide-react';
import { toast } from 'sonner';
import useDemoDates from '@/hooks/useDemoDates';
import AddDateDialog from './AddDateDialog';
import { AddToCalendarButton } from '@/components/native/ContactActionButtons';

const typeIcons = {
  hotel: Hotel,
  restaurant: Utensils,
  flight: Plane,
  rental: Car,
  event: Ticket,
  other: FileText
};

const ReservationsTab = () => {
  const { dates, loading, addDate, deleteDate } = useDemoDates('reservation');
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this reservation? You can always add it again later.')) return;
    await deleteDate(id);
  };

  const handleDateAdded = async (dateData) => {
    await addDate(dateData);
    setDialogOpen(false);
  };

  const copyConfirmation = (number) => {
    navigator.clipboard.writeText(number);
    toast.success('Confirmation number copied!');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">Reservations</h3>
          <p className="text-sm text-gray-500">Hotels, restaurants, flights, and more</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900 whitespace-nowrap flex-shrink-0"
          data-testid="add-reservation-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Reservation
        </Button>
      </div>

      {dates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Utensils className="h-12 w-12 mx-auto text-[#C5D9ED] mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">Keep Track of Reservations</h4>
          <p className="text-gray-600 mb-4">
            Hotels, restaurants, flights — all your booking confirmations in one place
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Reservation
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map((item) => {
            const TypeIcon = typeIcons[item.reservation_type] || typeIcons.other;
            
            return (
              <Card key={item.id} className="border-l-4 border-l-[#C5D9ED]">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-2 bg-[#C5D9ED]/30 rounded-lg">
                        <TypeIcon className="h-5 w-5 text-blue-700" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <h4 className="font-medium text-gray-900">{item.title}</h4>
                          {item.reservation_type && (
                            <Badge variant="secondary" className="capitalize">
                              {item.reservation_type}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Confirmation Number - Prominent Display */}
                        {item.confirmation_number && (
                          <div className="mb-2 p-2 bg-blue-50 rounded-lg border border-blue-200 inline-flex items-center gap-2">
                            <span className="text-sm text-blue-700 font-medium">
                              Confirmation: {item.confirmation_number}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyConfirmation(item.confirmation_number)}
                              className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                        
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-4 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              {new Date(item.date + 'T00:00:00').toLocaleDateString('en-US', { 
                                weekday: 'short', month: 'short', day: 'numeric' 
                              })}
                              {item.end_date && item.end_date !== item.date && (
                                <> - {new Date(item.end_date + 'T00:00:00').toLocaleDateString('en-US', { 
                                  weekday: 'short', month: 'short', day: 'numeric' 
                                })}</>
                              )}
                            </span>
                            {item.time && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {item.time}
                              </span>
                            )}
                          </div>
                          
                          {item.provider_company && (
                            <p className="text-gray-700 font-medium">{item.provider_company}</p>
                          )}
                          
                          {item.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" />
                              {item.location}
                            </span>
                          )}
                          
                          <div className="flex flex-wrap gap-2 mt-1">
                            {item.party_size && (
                              <Badge variant="outline">Party of {item.party_size}</Badge>
                            )}
                            {item.cost && (
                              <Badge variant="outline">${item.cost.toFixed(2)}</Badge>
                            )}
                          </div>
                          
                          {item.contact_phone && (
                            <a 
                              href={`tel:${item.contact_phone}`}
                              className="flex items-center gap-1 text-gray-500 hover:text-blue-600"
                            >
                              <Phone className="h-3.5 w-3.5" />
                              <span>{item.contact_phone}</span>
                            </a>
                          )}
                          
                          {item.original_files && item.original_files.length > 0 && (
                            <div className="flex items-center gap-1 text-blue-600 mt-1">
                              <FileText className="h-3.5 w-3.5" />
                              {item.original_files.length} attached file(s)
                            </div>
                          )}
                          
                          {item.notes && (
                            <p className="text-gray-500 italic mt-1">{item.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <AddToCalendarButton
                        dateRecord={{
                          title: item.title,
                          date: item.date,
                          time: item.time,
                          location: item.location || '',
                          type: 'reservation',
                          all_day: !item.time,
                          reminder_minutes: 120, // 2 hours before
                          notes: item.confirmation_number ? `Confirmation: ${item.confirmation_number}` : ''
                        }}
                        size="sm"
                        variant="ghost"
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        data-testid={`add-to-calendar-${item.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AddDateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category="reservation"
        onSuccess={handleDateAdded}
      />
    </>
  );
};

export default ReservationsTab;
